import { Button, Div, Span, state } from '@codesuma/baseline'
import rates, { IRate, IRates, formatRound, arrow } from '../../../services/rates'
import style from './index.module.css'

// The prices the reader follows, running along the top of the calendar. The
// home page places it and opens the prices page on a tap. Nothing at all
// when it is switched off there, or nothing is picked, or the phone is
// offline and the prices are more than half an hour old: old figures with
// no way to refresh them are worse than none.
//
// The run is drawn twice, end to end, and the pair slides one run's width
// and starts again: the second copy is then exactly where the first began,
// so the loop has no seam. It travels rightward, the way Persian reads, so
// each price comes in from the left after the one before it. A run short
// enough to fit stands still instead: a loop of it would show a price twice.
const STALE = 30 * 60000

export const RatesTicker = () => {
    const base = Button()
    base.addClass(style.base)
    base.attr('aria-label', 'قیمت ارز و طلا')

    const track = Div()
    track.addClass(style.track)
    const runs = [Div(), Div()]
    runs.forEach(run => run.addClass(style.run))
    track.append(...runs)
    base.append(track)

    const item = (r: IRate) => {
        const el = Span()
        el.addClass(style.item)
        const name = Span(r.name)
        name.addClass(style.name)
        el.append(name, Span(formatRound(r.price)))
        // Which way, and no more: the figures are on the prices page.
        if (r.change) {
            const change = Span(arrow(r))
            change.addClass(style.change, r.change > 0 ? style.up : style.down)
            el.append(change)
        }
        return el
    }

    let all: IRate[] = []
    let updated = 0
    const stale = () => !navigator.onLine && Date.now() - updated > STALE
    const render = () => {
        const picked = rates.isOn() ? all.filter(r => rates.isShown(r.id)) : []
        base.toggleClass(style.empty, !picked.length || stale())
        for (const run of runs) {
            run.empty()
            run.append(...picked.map(item))
        }
    }

    // Measured again whenever either side changes size: the run when prices
    // are picked or the fonts arrive, the strip when the text size changes.
    // The run's width also sets the loop's time, so the pace is the same 40px
    // a second however long the run is.
    const fit = () => {
        const width = runs[0].el.offsetWidth
        base.toggleClass(style.still, width <= base.el.offsetWidth)
        track.style({ animationDuration: `${width / 40}s` })
    }
    const observer = new ResizeObserver(fit)
    observer.observe(base.el)
    observer.observe(runs[0].el)

    // Drawn at once from the prices saved last time, then asked for again now
    // and every ten minutes while the calendar is open. Drawn again when the
    // phone goes offline or comes back, and the moment the prices turn half an
    // hour old, in case it is offline by then.
    let timer = 0
    const show = (found: IRates) => {
        all = found.rates
        updated = found.updated
        render()
        clearTimeout(timer)
        timer = window.setTimeout(render, updated + STALE - Date.now())
    }
    show(rates.saved())
    const refresh = () => rates.load().then(show)
    refresh()
    const poll = window.setInterval(refresh, 10 * 60000)
    state.on('rates-ticker', render)
    window.addEventListener('online', render)
    window.addEventListener('offline', render)
    base.on('unmounted', () => {
        state.off('rates-ticker', render)
        window.removeEventListener('online', render)
        window.removeEventListener('offline', render)
        clearTimeout(timer)
        clearInterval(poll)
        observer.disconnect()
    })

    return base
}
